import * as React from "react"

import { EmptyState } from "@/components/ui/alert"

import {
  MASTER_KIND_LABEL,
  MASTER_SOURCE_LABEL,
  type EvalMasterFix,
  type EvalMasterMapping,
} from "../eval-runs-data"

import { PdfViewer } from "./pdf-viewer"
import { usePdfDocument } from "./use-pdf-document"

// The master a label was compared against: which one, where it came from and how it was matched,
// above the master PDF itself in the same viewer as the label, with nothing drawn over it.

const REG_SOURCE_LABEL: Readonly<Record<string, string>> = {
  label_printed: "printed on the label",
  expert_comment: "named in the expert's comment",
  client_folder: "from the client's folder",
  reg_map_reviewer: "from the reviewer's registration map",
  ppls_transfer: "from a PPLS transfer",
}

const RULE_LABEL: Readonly<Record<string, string>> = {
  review_date: "the version in force when the expert reviewed the label",
  client_supplied: "the version the client supplied",
}

const NO_MARKS = [] as const
const NO_COUNTS: ReadonlyMap<string, number> = new Map()
const ignore = () => undefined

function Row(props: { term: string; children: React.ReactNode }) {
  return (
    <div className="flex min-w-0 gap-2">
      <dt className="shrink-0 text-text-3">{props.term}</dt>
      <dd className="min-w-0 break-words text-text">{props.children}</dd>
    </div>
  )
}

function Fix({ fix }: { fix: EvalMasterFix }) {
  return (
    <li className="min-w-0 break-words">
      <span className="text-text">{fix.what}</span>
      {fix.serious && <span className="ml-1 text-err">(serious)</span>}
      {fix.date && <span className="ml-1 text-text-3">· {fix.date}</span>}
      {fix.why && <p className="text-text-2">{fix.why}</p>}
      {fix.evidence.length > 0 && (
        <ul className="ml-4 list-disc text-text-3">
          {fix.evidence.map((line, i) => (
            <li key={i}>{line}</li>
          ))}
        </ul>
      )}
    </li>
  )
}

function MasterDetails({ master }: { master: EvalMasterMapping }) {
  const regSource = master.regSource
    ? (REG_SOURCE_LABEL[master.regSource] ?? master.regSource)
    : null
  const rule = master.rule ? (RULE_LABEL[master.rule] ?? master.rule) : null
  return (
    <>
      <dl className="grid grid-cols-1 gap-x-4 gap-y-1 sm:grid-cols-2">
        <Row term="Registration">
          <span className="font-mono">{master.regNr ?? "not recorded"}</span>
          {regSource && <span className="text-text-3"> · {regSource}</span>}
        </Row>
        <Row term="Master date">{master.masterDate ?? "not recorded"}</Row>
        <Row term="Source">{MASTER_SOURCE_LABEL[master.source]}</Row>
        <Row term="Matched">
          {MASTER_KIND_LABEL[master.kind]}
          {rule && <span className="text-text-3"> · {rule}</span>}
        </Row>
        <div className="sm:col-span-2">
          <Row term="File">
            {master.masterFile}
            {master.supplements.length > 0 && (
              <span className="text-text-3"> · with {master.supplements.join(", ")}</span>
            )}
          </Row>
        </div>
      </dl>
      {master.kind === "auto_fixed" && master.fixes.length > 0 && (
        <div className="mt-2 border-t border-border pt-2">
          <p className="text-text-3">What we changed from the client&apos;s mapping</p>
          <ul className="mt-1 flex flex-col gap-1.5">
            {master.fixes.map((fix, i) => (
              <Fix key={i} fix={fix} />
            ))}
          </ul>
        </div>
      )}
    </>
  )
}

/** One line for a narrow pane: registration · master date · source. */
export function masterSummary(master: EvalMasterMapping): string {
  return [
    master.regNr ?? "Registration not recorded",
    master.masterDate ?? "undated",
    MASTER_SOURCE_LABEL[master.source],
  ].join(" · ")
}

const HEADER_CLASS = "mb-2 rounded-md border border-border bg-surface px-3 py-2 text-caption"

/**
 * Which master, from where, and how it was matched. Compact (side by side, where it takes height
 * from both PDFs) it is one line, the rest behind a native disclosure.
 */
export function MasterHeader({
  master,
  compact = false,
}: {
  master: EvalMasterMapping
  compact?: boolean
}) {
  if (!compact) {
    return (
      <section aria-label="Master" className={HEADER_CLASS}>
        <MasterDetails master={master} />
      </section>
    )
  }
  return (
    <section aria-label="Master" className={HEADER_CLASS}>
      <details className="group min-w-0">
        <summary className="flex cursor-pointer flex-wrap items-baseline gap-x-2 rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-brand/40">
          <span className="min-w-0 break-words text-text">{masterSummary(master)}</span>
          <span className="text-text-3 underline-offset-2 group-open:hidden hover:underline">
            Details
          </span>
          <span className="hidden text-text-3 underline-offset-2 group-open:inline hover:underline">
            Hide details
          </span>
        </summary>
        <div className="mt-2">
          <MasterDetails master={master} />
        </div>
      </details>
    </section>
  )
}

export interface MasterPaneProps {
  runId: number
  mid: string
  master: EvalMasterMapping | null
  hasMasterPdf: boolean
  /** Side by side: the header collapses to one line. */
  compact?: boolean
  /** The page to open on, read once when the pane mounts. */
  openAt?: number | null
  /** The page the label asks for while the master follows it. */
  goTo?: { key: string; page: number } | null
  /** A quiet line beside the master's pager. */
  note?: React.ReactNode
}

/** Mounted only while the master is shown, so its PDF is fetched only then. */
export function MasterPane(props: MasterPaneProps) {
  const { runId, mid, master, hasMasterPdf } = props
  const pdf = usePdfDocument(runId, mid, hasMasterPdf, "master")
  const [page, setPage] = React.useState(0)
  const [opening] = React.useState(() =>
    props.openAt != null ? { key: "open", page: props.openAt } : null,
  )
  return (
    <div className="min-w-0">
      {master ? (
        <MasterHeader master={master} compact={props.compact ?? false} />
      ) : (
        <p className="mb-2 rounded-md border border-border bg-surface px-3 py-2 text-caption text-text-2">
          This run did not record which master the label was compared against.
        </p>
      )}
      {hasMasterPdf ? (
        <PdfViewer
          document="master"
          hasPdf
          pdf={pdf}
          page={page}
          setPage={setPage}
          marks={NO_MARKS}
          findings={NO_MARKS}
          selection={null}
          onSelect={ignore}
          remarkCounts={NO_COUNTS}
          card={null}
          goTo={props.goTo ?? opening}
          note={props.note}
        />
      ) : (
        master && (
          <EmptyState title="No master PDF" body="The master PDF was not uploaded with this run." />
        )
      )}
    </div>
  )
}
